import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FiAlertCircle,
  FiBarChart2,
  FiCalendar,
  FiCheck,
  FiEdit3,
  FiInfo,
  FiRefreshCw,
  FiSliders,
  FiX,
} from "react-icons/fi";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useCrossTabDataRefreshToken from "../hooks/useCrossTabDataRefresh";
import { readStoredUser } from "../utils/sessionUser";
import "../ClientAllocations.css";

const DIRECT_CLIENT_VALUE = "__direct__";
const SUMMARY_CLIENT_VALUE = "__summary__";
const HORIZON_DAYS = 7;

const CLIENT_ALLOCATIONS_MESSAGES = {
  en: {
    title: "Category Sales Control",
    purpose:
      "Set how many rooms each external agency or sales channel may sell per category and night. Sales count toward the reservation's external agency, regardless of the rate used. When a limit is reached, Pilcore closes its applicable rates automatically; it does not reserve physical inventory.",
    subtitles: {
      summaryLoading: "Loading overview...",
      summaryLoaded: (clients, categories, range) =>
        `Overview · ${clients} clients · ${categories} categories · ${range}`,
      loading: "Loading daily sales limits...",
      loaded: (clientName, categories, range) =>
        `${clientName} · ${categories} categories · ${range}`,
      empty: "Select a client to manage daily sales limits.",
    },
    view: {
      label: "Working mode",
      overview: "Overview",
      manage: "Manage limits",
      overviewHint: "Read-only attention list across every client and channel.",
      manageHint: "Choose one client or channel and edit its daily limits.",
      readOnly: "Your permissions allow viewing, but not changing sales limits.",
    },
    controls: {
      dateRange: "Period to analyse",
      quickRange: "Quick range",
    },
    rangePreset: (days) => `${days} days`,
    guide: {
      overviewTitle: "Start with the combinations that need attention",
      overviewText:
        "The overview only lists active limits, limits close to being reached and sold-out dates. Open Manage limits to make changes.",
      detailTitle: "Read each day as sold / client limit",
      detailText:
        "Select the limit to edit it. Zero closes this client's sales; setting it to the category inventory removes the additional restriction.",
      readOnlyTitle: "Read-only access",
    },
    labels: {
      fromDate: "From date",
      toDate: "To date",
      client: "Client",
      capValue: "Daily sales limit",
    },
    placeholders: {
      capExample: "e.g. 3",
      noClientsAvailable: "No clients available",
    },
    buttons: {
      today: "Today",
      refresh: "Refresh",
      bulkUpdate: "Apply limits by range",
      selectAll: "Select all",
      clear: "Clear",
      cancel: "Cancel",
      applyBulkUpdate: "Apply changes",
      applying: "Applying...",
      save: "Save",
      fullInventory: "Use full inventory",
    },
    modal: {
      ariaLabel: "Apply sales limits by range",
      title: "Apply one sales limit to several dates",
      subtitle: (clientLabel, fallbackLabel) => `${clientLabel || fallbackLabel} · Review the scope before applying`,
      stepRule: "1. Define the daily limit and period",
      ruleHint: "Choose whether to apply one limit or restore each category's full inventory.",
      limitRule: "Set one daily limit",
      limitRuleHint: "The same number will be applied to every selected category and date.",
      fullInventoryRule: "Use full inventory",
      fullInventoryRuleHint: "Removes the additional client limit using each category's own inventory.",
      stepCategories: "2. Choose the categories",
      categoryHint: "Nothing is selected automatically to prevent accidental changes.",
      close: "Close sales limit dialog",
      zeroWarning: "A limit of 0 closes this client's sales for every selected category and date.",
      preview: (categories, days, cells) =>
        `${categories} categories × ${days} days = ${cells} daily limits will be updated.`,
      categoriesSelected: (selected, total) => `${selected}/${total} categories selected`,
      categoryRooms: (rooms) => `${rooms} room${rooms === 1 ? "" : "s"}`,
    },
    stats: {
      summaryClients: "Clients monitored",
      detailRestricted: "Active daily limits",
      summaryRestricted: "Active daily limits",
      detailStopSell: "Limits reached",
      summaryCategories: "Categories monitored",
      detailNear: "One room from limit",
      summaryAutoStop: "Limits reached",
      summaryNear: "One room from limit",
      detailSoldVsCap: "Sold / allowed room-nights",
      summaryAutoStopValue: (rows, days) => `${rows} limits · ${days} days`,
      detailSoldVsCapValue: (sold, limit, pct) => `${sold}/${limit} (${pct}%)`,
    },
    legend: {
      openCapacity: "Full inventory allowed",
      nearLimit: "One room from limit",
      stopSellTriggered: "Limit reached",
      baselineHighlighted: "Additional limit active",
    },
    table: {
      client: "Client",
      readOnlyOverview: "Read-only overview",
      roomCategory: "Room Category",
      inventoryBaseline: "Physical inventory",
      summary: "Summary",
      clientAndCategory: "Client + category",
      clientRangeTotals: "Client range totals",
      categoriesInView: (count) => `${count} categories in view`,
      inventory: (rooms) => `Inventory: ${rooms} room${rooms === 1 ? "" : "s"}`,
      soldWord: "sold",
      cappedWord: "withheld room-nights",
      roomsLeftUnderCap: (rooms) => `${rooms} rooms left under cap`,
      noCapRestrictionsInRange: "No additional limits in this range",
      restrictedDaysStopSell: (restricted, stops) =>
        `${restricted} restricted day${restricted === 1 ? "" : "s"} | ${stops} day${stops === 1 ? "" : "s"} in auto stop sell`,
      soldRooms: (sold) => `Sold rooms: ${sold}`,
      cap: (limit) => `Sales limit: ${limit}`,
      baseline: (baseline) => `Inventory: ${baseline}`,
      cappedRooms: (capped) => `Withheld inventory: ${capped}`,
      noCapRestriction: "No additional limit (full category inventory allowed)",
      remaining: (remaining) => `Remaining: ${remaining}`,
      maxAllowed: (limit) => `Max allowed: ${limit}`,
      categoryAvailability: (available) => `Category availability: ${available}`,
      autoStopActive: "Limit reached",
      sellingAllowed: "Selling is still allowed",
      noCap: "Full inventory",
      soldStatus: (sold, total) => `${sold}/${total} sold`,
      maxCapForCategory: (max) => `Maximum: ${max}. Enter ${max} to remove the additional limit.`,
      capButton: (limit) => `Limit ${limit}`,
      stopSellAuto: "Limit reached",
      overLimit: (count) => `${count} over limit`,
      left: (count) => `${count} left`,
      noCapMeta: (sold, total) => `Full inventory · ${sold}/${total} sold`,
      remainingUsed: (remainingLabel, pct) => `${remainingLabel} | ${pct}% used`,
      soldLimitLabel: "sold / limit",
      soldInventoryLabel: "sold / inventory",
      sellableNow: (count) => `${count} sellable now`,
      editLimit: "Edit limit",
      noData: "No data",
      overviewDate: "Date",
      sold: "Sold",
      limit: "Limit",
      hotelAvailability: "Category availability",
      sellable: "Sellable",
      status: "Status",
      action: "Action",
      manage: "Manage",
      statusActive: "Active limit",
      statusNear: "One room left",
      statusReached: "Limit reached",
      statusSoldOut: "Category sold out",
      statusUnknown: "Availability unknown",
      zeroLimitWarning: "0 closes this client's sales for this day.",
    },
    states: {
      summaryLoadingTitle: "Loading overview...",
      summaryLoadingMeta: "Checking daily limits by client and category.",
      summaryEmptyTitle: "No summary data available",
      summaryEmptyMeta: "No client/category rows found in this range. Try another start date.",
      attentionEmptyTitle: "No sales limits need attention",
      attentionEmptyMeta: "There are no active, near or reached limits in this period.",
      loadingTitle: "Loading client allocations...",
      loadingMeta: "Please wait while we prepare the matrix.",
      selectClientTitle: "Select a client",
      selectClientMeta: "Choose one client to configure daily sales limits.",
      emptyTitle: "No allocation data available",
      emptyMeta: "There are no categories with active rates for this client in the selected range.",
    },
    alerts: {
      loadAllocations: "Could not load client allocations.",
      loadSummary: "Could not load summary allocations.",
      loadClients: "Could not load clients or sales channels.",
      loadCategories: "Could not load room categories.",
      partialSummary: (failed, total) =>
        `${failed} of ${total} clients could not be loaded. The overview shows the remaining data.`,
      bulkCapInteger: "Sales limit must be an integer greater than or equal to 0.",
      validDateRange: "Select a valid date range.",
      selectCategory: "Select at least one room category.",
      capExceedsInventory: (names, hasMore) =>
        `Sales limit exceeds inventory for: ${names}${hasMore ? "..." : ""}.`,
      noDatesToUpdate: "No dates in range to update.",
      bulkFailed: (failed, total, stayDate, categoryId, message) =>
        `Failed ${failed}/${total}. Example ${stayDate}, category ${categoryId}: ${message}`,
      maxRoomsRequired: "Max rooms is required. Use 0 to block sales for the day.",
      maxRoomsInteger: "Max rooms must be an integer value greater than or equal to 0.",
      validateCap: "Could not validate this sales limit. Please refresh and try again.",
      capCannotExceed: (inventory) =>
        `Sales limit cannot exceed category inventory (${inventory} room${inventory === 1 ? "" : "s"}).`,
      saveCap: "Could not save the sales limit for this day.",
    },
    toasts: {
      bulkApplied: (count) => `Bulk update applied to ${count} daily limit${count === 1 ? "" : "s"}.`,
      capSaved: "Sales limit saved successfully.",
    },
    fallback: {
      summary: "Summary",
      directReservation: "Direct Reservation",
      client: (index) => `Client ${index}`,
      unknownClient: "Unknown client",
      category: (id) => `Category ${id}`,
      clientLabel: "Client",
      unknownError: "Unknown error",
    },
  },
  es: {
    title: "Control de ventas por categor\u00eda",
    purpose:
      "Define cu\u00e1ntas habitaciones puede vender cada agencia externa o canal por categor\u00eda y noche. Las ventas cuentan para la agencia externa de la reserva, independientemente de la tarifa utilizada. Al alcanzar un l\u00edmite, Pilcore cierra autom\u00e1ticamente sus tarifas aplicables; no reserva inventario f\u00edsico.",
    subtitles: {
      summaryLoading: "Cargando vista general...",
      summaryLoaded: (clients, categories, range) =>
        `Vista general · ${clients} clientes · ${categories} categor\u00edas · ${range}`,
      loading: "Cargando l\u00edmites diarios de venta...",
      loaded: (clientName, categories, range) =>
        `${clientName} · ${categories} categor\u00edas · ${range}`,
      empty: "Selecciona un cliente para gestionar sus l\u00edmites diarios de venta.",
    },
    view: {
      label: "Modo de trabajo",
      overview: "Vista general",
      manage: "Gestionar l\u00edmites",
      overviewHint: "Lista de atenci\u00f3n de solo lectura para todos los clientes y canales.",
      manageHint: "Elige un cliente o canal y edita sus l\u00edmites diarios.",
      readOnly: "Tus permisos permiten consultar, pero no modificar los l\u00edmites de venta.",
    },
    controls: {
      dateRange: "Periodo a analizar",
      quickRange: "Rango r\u00e1pido",
    },
    rangePreset: (days) => `${days} d\u00edas`,
    guide: {
      overviewTitle: "Empieza por las combinaciones que requieren atenci\u00f3n",
      overviewText:
        "La vista general solo muestra l\u00edmites activos, l\u00edmites a punto de alcanzarse y fechas agotadas. Abre Gestionar l\u00edmites para hacer cambios.",
      detailTitle: "Lee cada d\u00eda como vendidas / l\u00edmite del cliente",
      detailText:
        "Selecciona el l\u00edmite para editarlo. Cero cierra las ventas de este cliente; igualarlo al inventario elimina la restricci\u00f3n adicional.",
      readOnlyTitle: "Acceso de solo lectura",
    },
    labels: {
      fromDate: "Desde",
      toDate: "Hasta",
      client: "Cliente",
      capValue: "L\u00edmite diario de venta",
    },
    placeholders: {
      capExample: "p. ej. 3",
      noClientsAvailable: "No hay clientes disponibles",
    },
    buttons: {
      today: "Hoy",
      refresh: "Actualizar",
      bulkUpdate: "Aplicar l\u00edmites por rango",
      selectAll: "Seleccionar todo",
      clear: "Limpiar",
      cancel: "Cancelar",
      applyBulkUpdate: "Aplicar cambios",
      applying: "Aplicando...",
      save: "Guardar",
      fullInventory: "Usar inventario completo",
    },
    modal: {
      ariaLabel: "Aplicar l\u00edmites de venta por rango",
      title: "Aplicar un mismo l\u00edmite a varias fechas",
      subtitle: (clientLabel, fallbackLabel) => `${clientLabel || fallbackLabel} · Revisa el alcance antes de aplicar`,
      stepRule: "1. Define el l\u00edmite diario y el periodo",
      ruleHint: "Elige entre aplicar un mismo l\u00edmite o recuperar el inventario completo de cada categor\u00eda.",
      limitRule: "Fijar un l\u00edmite diario",
      limitRuleHint: "Se aplicar\u00e1 el mismo n\u00famero a cada categor\u00eda y fecha seleccionada.",
      fullInventoryRule: "Usar inventario completo",
      fullInventoryRuleHint: "Elimina el l\u00edmite adicional usando el inventario propio de cada categor\u00eda.",
      stepCategories: "2. Elige las categor\u00edas",
      categoryHint: "No se selecciona ninguna autom\u00e1ticamente para evitar cambios accidentales.",
      close: "Cerrar el di\u00e1logo de l\u00edmites de venta",
      zeroWarning: "Un l\u00edmite de 0 cierra las ventas de este cliente en todas las categor\u00edas y fechas seleccionadas.",
      preview: (categories, days, cells) =>
        `${categories} categor\u00edas × ${days} d\u00edas = se actualizar\u00e1n ${cells} l\u00edmites diarios.`,
      categoriesSelected: (selected, total) => `${selected}/${total} categor\u00edas seleccionadas`,
      categoryRooms: (rooms) => `${rooms} ${rooms === 1 ? "habitaci\u00f3n" : "habitaciones"}`,
    },
    stats: {
      summaryClients: "Clientes monitorizados",
      detailRestricted: "L\u00edmites diarios activos",
      summaryRestricted: "L\u00edmites diarios activos",
      detailStopSell: "L\u00edmites alcanzados",
      summaryCategories: "Categor\u00edas monitorizadas",
      detailNear: "A una habitaci\u00f3n del l\u00edmite",
      summaryAutoStop: "L\u00edmites alcanzados",
      summaryNear: "A una habitaci\u00f3n del l\u00edmite",
      detailSoldVsCap: "Vendidas / noches-habitaci\u00f3n permitidas",
      summaryAutoStopValue: (rows, days) => `${rows} l\u00edmites · ${days} d\u00edas`,
      detailSoldVsCapValue: (sold, limit, pct) => `${sold}/${limit} (${pct}%)`,
    },
    legend: {
      openCapacity: "Inventario completo permitido",
      nearLimit: "A una habitaci\u00f3n del l\u00edmite",
      stopSellTriggered: "L\u00edmite alcanzado",
      baselineHighlighted: "L\u00edmite adicional activo",
    },
    table: {
      client: "Cliente",
      readOnlyOverview: "Vista solo lectura",
      roomCategory: "Categor\u00eda",
      inventoryBaseline: "Inventario f\u00edsico",
      summary: "Resumen",
      clientAndCategory: "Cliente + categor\u00eda",
      clientRangeTotals: "Totales del cliente en rango",
      categoriesInView: (count) => `${count} categor\u00edas visibles`,
      inventory: (rooms) => `Inventario: ${rooms} ${rooms === 1 ? "habitaci\u00f3n" : "habitaciones"}`,
      soldWord: "vendidas",
      cappedWord: "noches-habitaci\u00f3n retenidas",
      roomsLeftUnderCap: (rooms) => `${rooms} habitaciones libres bajo tope`,
      noCapRestrictionsInRange: "Sin l\u00edmites adicionales en el rango",
      restrictedDaysStopSell: (restricted, stops) =>
        `${restricted} d\u00eda${restricted === 1 ? "" : "s"} restringido${restricted === 1 ? "" : "s"} | ${stops} d\u00eda${stops === 1 ? "" : "s"} en auto stop sell`,
      soldRooms: (sold) => `Habitaciones vendidas: ${sold}`,
      cap: (limit) => `L\u00edmite de venta: ${limit}`,
      baseline: (baseline) => `Inventario: ${baseline}`,
      cappedRooms: (capped) => `Inventario retenido: ${capped}`,
      noCapRestriction: "Sin l\u00edmite adicional (inventario completo permitido)",
      remaining: (remaining) => `Restantes: ${remaining}`,
      maxAllowed: (limit) => `M\u00e1ximo permitido: ${limit}`,
      categoryAvailability: (available) => `Disponibilidad de la categor\u00eda: ${available}`,
      autoStopActive: "L\u00edmite alcanzado",
      sellingAllowed: "La venta sigue permitida",
      noCap: "Inventario completo",
      soldStatus: (sold, total) => `${sold}/${total} vendidas`,
      maxCapForCategory: (max) => `M\u00e1ximo: ${max}. Introduce ${max} para eliminar el l\u00edmite adicional.`,
      capButton: (limit) => `L\u00edmite ${limit}`,
      stopSellAuto: "L\u00edmite alcanzado",
      overLimit: (count) => `${count} por encima del l\u00edmite`,
      left: (count) => `${count} libres`,
      noCapMeta: (sold, total) => `Inventario completo · ${sold}/${total} vendidas`,
      remainingUsed: (remainingLabel, pct) => `${remainingLabel} | ${pct}% usado`,
      soldLimitLabel: "vendidas / l\u00edmite",
      soldInventoryLabel: "vendidas / inventario",
      sellableNow: (count) => `${count} vendibles ahora`,
      editLimit: "Editar l\u00edmite",
      noData: "Sin datos",
      overviewDate: "Fecha",
      sold: "Vendidas",
      limit: "L\u00edmite",
      hotelAvailability: "Disponibilidad categor\u00eda",
      sellable: "Vendibles",
      status: "Estado",
      action: "Acci\u00f3n",
      manage: "Gestionar",
      statusActive: "L\u00edmite activo",
      statusNear: "Queda una",
      statusReached: "L\u00edmite alcanzado",
      statusSoldOut: "Categor\u00eda agotada",
      statusUnknown: "Disponibilidad sin datos",
      zeroLimitWarning: "0 cierra las ventas de este cliente durante este d\u00eda.",
    },
    states: {
      summaryLoadingTitle: "Cargando vista general...",
      summaryLoadingMeta: "Revisando los l\u00edmites diarios por cliente y categor\u00eda.",
      summaryEmptyTitle: "No hay datos de resumen",
      summaryEmptyMeta: "No se han encontrado filas de cliente/categor\u00eda en este rango. Prueba otra fecha de inicio.",
      attentionEmptyTitle: "Ning\u00fan l\u00edmite de venta requiere atenci\u00f3n",
      attentionEmptyMeta: "No hay l\u00edmites activos, cercanos o alcanzados en este periodo.",
      loadingTitle: "Cargando asignaciones del cliente...",
      loadingMeta: "Espera mientras preparamos la matriz.",
      selectClientTitle: "Selecciona un cliente",
      selectClientMeta: "Elige un cliente para configurar l\u00edmites diarios de venta.",
      emptyTitle: "No hay datos de asignaci\u00f3n",
      emptyMeta: "No hay categor\u00edas con tarifas activas para este cliente en el rango seleccionado.",
    },
    alerts: {
      loadAllocations: "No se han podido cargar las asignaciones del cliente.",
      loadSummary: "No se han podido cargar las asignaciones resumen.",
      loadClients: "No se han podido cargar los clientes o canales de venta.",
      loadCategories: "No se han podido cargar las categor\u00edas de habitaci\u00f3n.",
      partialSummary: (failed, total) =>
        `No se han podido cargar ${failed} de ${total} clientes. La vista general muestra el resto de los datos.`,
      bulkCapInteger: "El l\u00edmite debe ser un entero mayor o igual que 0.",
      validDateRange: "Selecciona un rango de fechas v\u00e1lido.",
      selectCategory: "Selecciona al menos una categor\u00eda.",
      capExceedsInventory: (names, hasMore) =>
        `El l\u00edmite supera el inventario en: ${names}${hasMore ? "..." : ""}.`,
      noDatesToUpdate: "No hay fechas en el rango para actualizar.",
      bulkFailed: (failed, total, stayDate, categoryId, message) =>
        `Han fallado ${failed}/${total}. Ejemplo ${stayDate}, categor\u00eda ${categoryId}: ${message}`,
      maxRoomsRequired: "El m\u00e1ximo de habitaciones es obligatorio. Usa 0 para bloquear la venta del d\u00eda.",
      maxRoomsInteger: "El m\u00e1ximo de habitaciones debe ser un entero mayor o igual que 0.",
      validateCap: "No se ha podido validar este l\u00edmite. Actualiza e int\u00e9ntalo de nuevo.",
      capCannotExceed: (inventory) =>
        `El l\u00edmite no puede superar el inventario de la categor\u00eda (${inventory} ${inventory === 1 ? "habitaci\u00f3n" : "habitaciones"}).`,
      saveCap: "No se ha podido guardar el l\u00edmite de venta para este d\u00eda.",
    },
    toasts: {
      bulkApplied: (count) => `Actualizaci\u00f3n masiva aplicada en ${count} l\u00edmite${count === 1 ? "" : "s"} diario${count === 1 ? "" : "s"}.`,
      capSaved: "L\u00edmite de venta guardado correctamente.",
    },
    fallback: {
      summary: "Resumen",
      directReservation: "Reserva directa",
      client: (index) => `Cliente ${index}`,
      unknownClient: "Cliente desconocido",
      category: (id) => `Categor\u00eda ${id}`,
      clientLabel: "Cliente",
      unknownError: "Error desconocido",
    },
  },
  ca: {
    title: "Control de vendes per categoria",
    purpose:
      "Defineix quantes habitacions pot vendre cada ag\u00e8ncia externa o canal per categoria i nit. Les vendes compten per a l'ag\u00e8ncia externa de la reserva, independentment de la tarifa utilitzada. En assolir un l\u00edmit, Pilcore tanca autom\u00e0ticament les seves tarifes aplicables; no reserva inventari f\u00edsic.",
    subtitles: {
      summaryLoading: "Carregant vista general...",
      summaryLoaded: (clients, categories, range) =>
        `Vista general · ${clients} clients · ${categories} categories · ${range}`,
      loading: "Carregant l\u00edmits diaris de venda...",
      loaded: (clientName, categories, range) =>
        `${clientName} · ${categories} categories · ${range}`,
      empty: "Selecciona un client per gestionar els seus l\u00edmits diaris de venda.",
    },
    view: {
      label: "Mode de treball",
      overview: "Vista general",
      manage: "Gestionar l\u00edmits",
      overviewHint: "Llista d'atenci\u00f3 de nom\u00e9s lectura per a tots els clients i canals.",
      manageHint: "Tria un client o canal i edita els seus l\u00edmits diaris.",
      readOnly: "Els teus permisos permeten consultar, per\u00f2 no modificar els l\u00edmits de venda.",
    },
    controls: {
      dateRange: "Per\u00edode a analitzar",
      quickRange: "Rang r\u00e0pid",
    },
    rangePreset: (days) => `${days} dies`,
    guide: {
      overviewTitle: "Comen\u00e7a per les combinacions que requereixen atenci\u00f3",
      overviewText:
        "La vista general nom\u00e9s mostra l\u00edmits actius, l\u00edmits a punt d'assolir-se i dates esgotades. Obre Gestionar l\u00edmits per fer canvis.",
      detailTitle: "Llegeix cada dia com venudes / l\u00edmit del client",
      detailText:
        "Selecciona el l\u00edmit per editar-lo. Zero tanca les vendes d'aquest client; igualar-lo a l'inventari elimina la restricci\u00f3 addicional.",
      readOnlyTitle: "Acc\u00e9s de nom\u00e9s lectura",
    },
    labels: {
      fromDate: "Des de",
      toDate: "Fins a",
      client: "Client",
      capValue: "L\u00edmit diari de venda",
    },
    placeholders: {
      capExample: "p. ex. 3",
      noClientsAvailable: "No hi ha clients disponibles",
    },
    buttons: {
      today: "Avui",
      refresh: "Actualitzar",
      bulkUpdate: "Aplicar l\u00edmits per rang",
      selectAll: "Seleccionar-ho tot",
      clear: "Netejar",
      cancel: "Cancel.lar",
      applyBulkUpdate: "Aplicar canvis",
      applying: "Aplicant...",
      save: "Desar",
      fullInventory: "Usar l'inventari complet",
    },
    modal: {
      ariaLabel: "Aplicar l\u00edmits de venda per rang",
      title: "Aplicar un mateix l\u00edmit a diverses dates",
      subtitle: (clientLabel, fallbackLabel) => `${clientLabel || fallbackLabel} · Revisa l'abast abans d'aplicar`,
      stepRule: "1. Defineix el l\u00edmit diari i el per\u00edode",
      ruleHint: "Tria entre aplicar un mateix l\u00edmit o recuperar l'inventari complet de cada categoria.",
      limitRule: "Fixar un l\u00edmit diari",
      limitRuleHint: "S'aplicar\u00e0 el mateix nombre a cada categoria i data seleccionada.",
      fullInventoryRule: "Usar inventari complet",
      fullInventoryRuleHint: "Elimina el l\u00edmit addicional usant l'inventari propi de cada categoria.",
      stepCategories: "2. Tria les categories",
      categoryHint: "No se'n selecciona cap autom\u00e0ticament per evitar canvis accidentals.",
      close: "Tancar el di\u00e0leg de l\u00edmits de venda",
      zeroWarning: "Un l\u00edmit de 0 tanca les vendes d'aquest client en totes les categories i dates seleccionades.",
      preview: (categories, days, cells) =>
        `${categories} categories × ${days} dies = s'actualitzaran ${cells} l\u00edmits diaris.`,
      categoriesSelected: (selected, total) => `${selected}/${total} categories seleccionades`,
      categoryRooms: (rooms) => `${rooms} ${rooms === 1 ? "habitaci\u00f3" : "habitacions"}`,
    },
    stats: {
      summaryClients: "Clients monitoritzats",
      detailRestricted: "L\u00edmits diaris actius",
      summaryRestricted: "L\u00edmits diaris actius",
      detailStopSell: "L\u00edmits assolits",
      summaryCategories: "Categories monitoritzades",
      detailNear: "A una habitaci\u00f3 del l\u00edmit",
      summaryAutoStop: "L\u00edmits assolits",
      summaryNear: "A una habitaci\u00f3 del l\u00edmit",
      detailSoldVsCap: "Venudes / nits-habitaci\u00f3 permeses",
      summaryAutoStopValue: (rows, days) => `${rows} l\u00edmits · ${days} dies`,
      detailSoldVsCapValue: (sold, limit, pct) => `${sold}/${limit} (${pct}%)`,
    },
    legend: {
      openCapacity: "Inventari complet perm\u00e8s",
      nearLimit: "A una habitaci\u00f3 del l\u00edmit",
      stopSellTriggered: "L\u00edmit assolit",
      baselineHighlighted: "L\u00edmit addicional actiu",
    },
    table: {
      client: "Client",
      readOnlyOverview: "Vista nom\u00e9s lectura",
      roomCategory: "Categoria",
      inventoryBaseline: "Inventari f\u00edsic",
      summary: "Resum",
      clientAndCategory: "Client + categoria",
      clientRangeTotals: "Totals del client en rang",
      categoriesInView: (count) => `${count} categories visibles`,
      inventory: (rooms) => `Inventari: ${rooms} ${rooms === 1 ? "habitaci\u00f3" : "habitacions"}`,
      soldWord: "venudes",
      cappedWord: "nits-habitaci\u00f3 retingudes",
      roomsLeftUnderCap: (rooms) => `${rooms} habitacions lliures sota topall`,
      noCapRestrictionsInRange: "Sense l\u00edmits addicionals en el rang",
      restrictedDaysStopSell: (restricted, stops) =>
        `${restricted} dia${restricted === 1 ? "" : "s"} restringit${restricted === 1 ? "" : "s"} | ${stops} dia${stops === 1 ? "" : "s"} en auto stop sell`,
      soldRooms: (sold) => `Habitacions venudes: ${sold}`,
      cap: (limit) => `L\u00edmit de venda: ${limit}`,
      baseline: (baseline) => `Inventari: ${baseline}`,
      cappedRooms: (capped) => `Inventari retingut: ${capped}`,
      noCapRestriction: "Sense l\u00edmit addicional (inventari complet perm\u00e8s)",
      remaining: (remaining) => `Restants: ${remaining}`,
      maxAllowed: (limit) => `M\u00e0xim perm\u00e8s: ${limit}`,
      categoryAvailability: (available) => `Disponibilitat de la categoria: ${available}`,
      autoStopActive: "L\u00edmit s'ha assolit",
      sellingAllowed: "La venda continua permesa",
      noCap: "Inventari complet",
      soldStatus: (sold, total) => `${sold}/${total} venudes`,
      maxCapForCategory: (max) => `M\u00e0xim: ${max}. Introdueix ${max} per eliminar el l\u00edmit addicional.`,
      capButton: (limit) => `L\u00edmit ${limit}`,
      stopSellAuto: "L\u00edmit assolit",
      overLimit: (count) => `${count} per sobre del l\u00edmit`,
      left: (count) => `${count} lliures`,
      noCapMeta: (sold, total) => `Inventari complet · ${sold}/${total} venudes`,
      remainingUsed: (remainingLabel, pct) => `${remainingLabel} | ${pct}% utilitzat`,
      soldLimitLabel: "venudes / l\u00edmit",
      soldInventoryLabel: "venudes / inventari",
      sellableNow: (count) => `${count} vendibles ara`,
      editLimit: "Editar l\u00edmit",
      noData: "Sense dades",
      overviewDate: "Data",
      sold: "Venudes",
      limit: "L\u00edmit",
      hotelAvailability: "Disponibilitat categoria",
      sellable: "Vendibles",
      status: "Estat",
      action: "Acci\u00f3",
      manage: "Gestionar",
      statusActive: "L\u00edmit actiu",
      statusNear: "En queda una",
      statusReached: "L\u00edmit assolit",
      statusSoldOut: "Categoria esgotada",
      statusUnknown: "Disponibilitat sense dades",
      zeroLimitWarning: "0 tanca les vendes d'aquest client durant aquest dia.",
    },
    states: {
      summaryLoadingTitle: "Carregant vista general...",
      summaryLoadingMeta: "Revisant els l\u00edmits diaris per client i categoria.",
      summaryEmptyTitle: "No hi ha dades de resum",
      summaryEmptyMeta: "No s'han trobat files de client/categoria en aquest rang. Prova una altra data d'inici.",
      attentionEmptyTitle: "Cap l\u00edmit de venda requereix atenci\u00f3",
      attentionEmptyMeta: "No hi ha l\u00edmits actius, propers o assolits en aquest per\u00edode.",
      loadingTitle: "Carregant assignacions del client...",
      loadingMeta: "Espera mentre preparem la matriu.",
      selectClientTitle: "Selecciona un client",
      selectClientMeta: "Tria un client per configurar l\u00edmits diaris de venda.",
      emptyTitle: "No hi ha dades d'assignaci\u00f3",
      emptyMeta: "No hi ha categories amb tarifes actives per a aquest client en el rang seleccionat.",
    },
    alerts: {
      loadAllocations: "No s'han pogut carregar les assignacions del client.",
      loadSummary: "No s'han pogut carregar les assignacions resum.",
      loadClients: "No s'han pogut carregar els clients o canals de venda.",
      loadCategories: "No s'han pogut carregar les categories d'habitaci\u00f3.",
      partialSummary: (failed, total) =>
        `No s'han pogut carregar ${failed} de ${total} clients. La vista general mostra la resta de dades.`,
      bulkCapInteger: "El l\u00edmit ha de ser un enter major o igual que 0.",
      validDateRange: "Selecciona un rang de dates v\u00e0lid.",
      selectCategory: "Selecciona com a m\u00ednim una categoria.",
      capExceedsInventory: (names, hasMore) =>
        `El l\u00edmit supera l'inventari a: ${names}${hasMore ? "..." : ""}.`,
      noDatesToUpdate: "No hi ha dates en el rang per actualitzar.",
      bulkFailed: (failed, total, stayDate, categoryId, message) =>
        `Han fallat ${failed}/${total}. Exemple ${stayDate}, categoria ${categoryId}: ${message}`,
      maxRoomsRequired: "El m\u00e0xim d'habitacions \u00e9s obligatori. Usa 0 per bloquejar la venda del dia.",
      maxRoomsInteger: "El m\u00e0xim d'habitacions ha de ser un enter major o igual que 0.",
      validateCap: "No s'ha pogut validar aquest l\u00edmit. Actualitza i torna-ho a provar.",
      capCannotExceed: (inventory) =>
        `El l\u00edmit no pot superar l'inventari de la categoria (${inventory} ${inventory === 1 ? "habitaci\u00f3" : "habitacions"}).`,
      saveCap: "No s'ha pogut desar el l\u00edmit de venda per a aquest dia.",
    },
    toasts: {
      bulkApplied: (count) => `Actualitzaci\u00f3 massiva aplicada a ${count} l\u00edmit${count === 1 ? "" : "s"} diari${count === 1 ? "" : "s"}.`,
      capSaved: "L\u00edmit de venda desat correctament.",
    },
    fallback: {
      summary: "Resum",
      directReservation: "Reserva directa",
      client: (index) => `Client ${index}`,
      unknownClient: "Client desconegut",
      category: (id) => `Categoria ${id}`,
      clientLabel: "Client",
      unknownError: "Error desconegut",
    },
  },
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const addDaysIso = (isoDate, days) => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return isoDate;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const enumerateIsoDates = (startIso, endIso) => {
  const start = new Date(`${String(startIso || "").slice(0, 10)}T00:00:00Z`);
  const end = new Date(`${String(endIso || "").slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];

  const days = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
};

const toInt = (value, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.trunc(parsed);
};

const toClientQueryValue = (clientValue) => {
  if (!clientValue) return undefined;
  if (clientValue === SUMMARY_CLIENT_VALUE) return undefined;
  if (clientValue === DIRECT_CLIENT_VALUE) return "null";

  const parsed = Number(clientValue);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed;
};

const toClientPayloadValue = (clientValue) => {
  if (clientValue === SUMMARY_CLIENT_VALUE) return null;
  if (!clientValue || clientValue === DIRECT_CLIENT_VALUE) return null;

  const parsed = Number(clientValue);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

const toDayHeader = (isoDate, locale = undefined) => {
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;

  return date.toLocaleDateString(locale, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
};

const buildCellKey = (roomCategoryId, stayDate) => `${roomCategoryId}::${stayDate}`;

const getCellStatusKey = (cell) => {
  if (cell?.isLimitReached || cell?.isOverLimit) return "reached";
  if (cell?.isCategorySoldOut) return "soldOut";
  if (cell?.availableRooms == null) return "unknown";
  if (cell?.isNearLimit) return "near";
  if (cell?.isRestrictedCap) return "active";
  return "open";
};

const getCellToneClass = (cell) => {
  const status = getCellStatusKey(cell);
  if (status === "reached") return "is-stop";
  if (status === "soldOut" || status === "unknown") return "is-unavailable";
  if (status === "near") return "is-near";
  return "is-safe";
};

const getLocalizedRequestError = (error, fallback, languageId) => {
  const serverMessage = String(error?.response?.data?.error || "").trim();
  return languageId === "en" && serverMessage ? serverMessage : fallback;
};

const ClientAllocations = () => {
  const { themeId, languageId, language } = usePmsTheme();
  const isLightTheme = themeId === "light";
  const allocationCopy = CLIENT_ALLOCATIONS_MESSAGES[languageId] || CLIENT_ALLOCATIONS_MESSAGES.en;
  const calendarLocale = languageId === "en" ? undefined : language?.locale || undefined;
  const sortLocale = languageId === "en" ? undefined : language?.locale || undefined;
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "";
  const [currentUser, setCurrentUser] = useState(() => readStoredUser());
  const permissions = currentUser?.permissions || {};
  const canEditAllocations =
    Number(currentUser?.id) === 1 || Boolean(permissions["reservations.update"]);

  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(addDaysIso(todayIso(), HORIZON_DAYS - 1));
  const [selectedClient, setSelectedClient] = useState("");
  const [clients, setClients] = useState([]);
  const [roomCategoryMap, setRoomCategoryMap] = useState({});

  const [allocations, setAllocations] = useState([]);
  const [summaryAllocations, setSummaryAllocations] = useState([]);
  const [dates, setDates] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [clientsError, setClientsError] = useState("");
  const [categoriesError, setCategoriesError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [operationError, setOperationError] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingCellKey, setSavingCellKey] = useState("");
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkFromDate, setBulkFromDate] = useState("");
  const [bulkToDate, setBulkToDate] = useState("");
  const [bulkRuleMode, setBulkRuleMode] = useState("limit");
  const [bulkCapValue, setBulkCapValue] = useState("");
  const [bulkCategoryIds, setBulkCategoryIds] = useState([]);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkError, setBulkError] = useState("");

  const fetchSequenceRef = useRef(0);
  const summaryFetchSequenceRef = useRef(0);
  const toastTimerRef = useRef(null);
  const bulkModalRef = useRef(null);
  const bulkTriggerRef = useRef(null);
  const crossTabRefreshToken = useCrossTabDataRefreshToken();

  const normalizeRow = useCallback(
    (row) => {
      const roomCategoryId = toInt(row?.room_category_id, 0);
      const stayDate = String(row?.stay_date || "").slice(0, 10);

      const totalRooms = Math.max(0, toInt(row?.total_rooms, 0));
      const rawAvailability = row?.available_rooms;
      const hasAvailability =
        (typeof rawAvailability === "number" ||
          (typeof rawAvailability === "string" && rawAvailability.trim() !== "")) &&
        Number.isFinite(Number(rawAvailability));
      const availableRooms = hasAvailability ? Math.max(0, toInt(rawAvailability)) : null;
      const soldRooms = Math.max(0, toInt(row?.sold_rooms, 0));

      const rawLimit = row?.max_rooms_allowed;
      const hasExplicitLimit = rawLimit !== null && rawLimit !== undefined && rawLimit !== "";
      const baseLimit = hasExplicitLimit
        ? Math.max(0, toInt(rawLimit, 0))
        : Math.max(0, totalRooms);

      const isRestrictedCap = baseLimit < totalRooms;
      const remainingRooms = baseLimit - soldRooms;
      const ratioBase = isRestrictedCap ? baseLimit : totalRooms;
      const utilizationRatio = ratioBase > 0 ? Math.min(1, soldRooms / ratioBase) : soldRooms > 0 ? 1 : 0;
      const isLimitReached = isRestrictedCap && soldRooms >= baseLimit;
      const isNearLimit = isRestrictedCap && !isLimitReached && remainingRooms <= 1;
      const effectiveSellable = hasAvailability ? Math.max(
        0,
        Math.min(availableRooms, isRestrictedCap ? remainingRooms : availableRooms)
      ) : null;
      const isCategorySoldOut = hasAvailability && availableRooms <= 0;
      const isOverLimit = isRestrictedCap && soldRooms > baseLimit;

      const categoryName =
        row?.room_category?.name ||
        roomCategoryMap[roomCategoryId]?.name ||
        allocationCopy.fallback.category(roomCategoryId);

      return {
        roomCategoryId,
        stayDate,
        totalRooms,
        availableRooms,
        soldRooms,
        limitRooms: baseLimit,
        remainingRooms,
        utilizationRatio,
        isRestrictedCap,
        isLimitReached,
        isNearLimit,
        effectiveSellable,
        isCategorySoldOut,
        isOverLimit,
        categoryName,
      };
    },
    [allocationCopy.fallback, roomCategoryMap]
  );

  const fetchRoomCategories = useCallback(async () => {
    if (!businessUnitId) {
      setRoomCategoryMap({});
      setCategoriesError("");
      return;
    }

    setCategoriesError("");
    try {
      const response = await axios.get(`/api/room_categories?business_unit_id=${businessUnitId}`);
      const rows = Array.isArray(response.data) ? response.data : [];
      const nextMap = {};
      rows.forEach((category) => {
        const id = toInt(category?.id, 0);
        if (id > 0) nextMap[id] = category;
      });
      setRoomCategoryMap(nextMap);
    } catch (error) {
      setRoomCategoryMap({});
      setCategoriesError(allocationCopy.alerts.loadCategories);
      console.error("[ClientAllocations] Error loading room categories:", error);
    }
  }, [allocationCopy.alerts.loadCategories, businessUnitId]);

  const fetchClients = useCallback(async () => {
    if (!businessUnitId) {
      setClients([]);
      setSelectedClient("");
      setClientsError("");
      return;
    }

    setClientsError("");
    try {
      const response = await axios.get(
        `/api/client-category-allocation/clients?business_unit_id=${businessUnitId}`
      );
      const rows = Array.isArray(response.data) ? response.data : [];

      const mapped = rows
        .map((client, index) => {
          const isDirect = client?.id === null;
          const value = isDirect ? DIRECT_CLIENT_VALUE : String(client?.id ?? "");
          const label = isDirect
            ? allocationCopy.fallback.directReservation
            : String(client?.name || client?.agency_name || "").trim();

          if (!value) return null;
          return {
            value,
            label: label || (isDirect ? allocationCopy.fallback.directReservation : allocationCopy.fallback.client(index + 1)),
          };
        })
        .filter(Boolean);

      if (!mapped.some((client) => client.value === DIRECT_CLIENT_VALUE)) {
        mapped.push({ value: DIRECT_CLIENT_VALUE, label: allocationCopy.fallback.directReservation });
      }

      mapped.unshift({ value: SUMMARY_CLIENT_VALUE, label: allocationCopy.fallback.summary });

      mapped.sort((a, b) => {
        const rank = (value) => {
          if (value === SUMMARY_CLIENT_VALUE) return 0;
          if (value === DIRECT_CLIENT_VALUE) return 1;
          return 2;
        };
        const rankDiff = rank(a.value) - rank(b.value);
        if (rankDiff !== 0) return rankDiff;
        return a.label.localeCompare(b.label, sortLocale, { sensitivity: "base" });
      });

      setClients(mapped);
      setSelectedClient((current) => {
        if (current && mapped.some((client) => client.value === current)) return current;
        return mapped[0]?.value || "";
      });
    } catch (error) {
      console.error("[ClientAllocations] Error loading clients:", error);
      setClients([]);
      setSelectedClient("");
      setClientsError(allocationCopy.alerts.loadClients);
    }
  }, [allocationCopy.alerts.loadClients, allocationCopy.fallback, businessUnitId, sortLocale]);

  const fetchAllocations = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = ++fetchSequenceRef.current;
      if (!silent) setLoading(true);
      setLoadError("");

      if (!businessUnitId || !selectedClient || selectedClient === SUMMARY_CLIENT_VALUE) {
        setAllocations([]);
        setDates([]);
        if (!silent && requestId === fetchSequenceRef.current) setLoading(false);
        return;
      }

      try {
        const response = await axios.get("/api/client-category-allocation", {
          params: {
            business_unit_id: businessUnitId,
            start_date: startDate,
            end_date: endDate,
            client_id: toClientQueryValue(selectedClient),
          },
        });

        if (requestId !== fetchSequenceRef.current) return;

        const rows = Array.isArray(response.data)
          ? response.data
              .map(normalizeRow)
              .filter((row) => row.stayDate >= startDate && row.stayDate <= endDate)
          : [];
        const uniqueDates = [...new Set(rows.map((row) => row.stayDate))].filter(Boolean).sort();
        setAllocations(rows);
        setDates(uniqueDates);
      } catch (error) {
        if (requestId !== fetchSequenceRef.current) return;
        setAllocations([]);
        setDates([]);
        setLoadError(
          getLocalizedRequestError(error, allocationCopy.alerts.loadAllocations, languageId)
        );
      } finally {
        if (!silent && requestId === fetchSequenceRef.current) {
          setLoading(false);
        }
      }
    },
    [allocationCopy.alerts.loadAllocations, businessUnitId, endDate, languageId, normalizeRow, selectedClient, startDate]
  );

  const fetchSummaryAllocations = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = ++summaryFetchSequenceRef.current;
      if (!businessUnitId || clients.length === 0) {
        setSummaryAllocations([]);
        setDates([]);
        if (!silent) setSummaryLoading(false);
        return;
      }

      if (!silent) setSummaryLoading(true);
      setSummaryError("");

      try {
        const targets = clients
          .filter(
            (client) =>
              String(client?.value || "").trim().length > 0 &&
              client.value !== SUMMARY_CLIENT_VALUE
          )
          .map((client) => ({ value: client.value, label: client.label || allocationCopy.fallback.unknownClient }));

        const requests = targets.map(async (client) => {
          const response = await axios.get("/api/client-category-allocation", {
            params: {
              business_unit_id: businessUnitId,
              start_date: startDate,
              end_date: endDate,
              client_id: toClientQueryValue(client.value),
            },
          });

          const rows = Array.isArray(response.data) ? response.data : [];
          return rows
            .map((row) => normalizeRow(row))
            .filter((row) => row.stayDate >= startDate && row.stayDate <= endDate)
            .map((row) => ({
              ...row,
              clientValue: client.value,
              clientLabel: client.label,
            }));
        });

        const results = await Promise.allSettled(requests);
        if (requestId !== summaryFetchSequenceRef.current) return;
        const successful = results.filter((result) => result.status === "fulfilled");
        const failed = results.filter((result) => result.status === "rejected");

        if (successful.length === 0 && failed.length > 0) {
          throw failed[0].reason;
        }

        const allRows = successful.flatMap((result) => result.value);
        const dedupedByCell = new Map();
        allRows.forEach((row) => {
          const key = `${row.clientValue}::${row.roomCategoryId}::${row.stayDate}`;
          if (!dedupedByCell.has(key)) dedupedByCell.set(key, row);
        });

        const merged = Array.from(dedupedByCell.values()).sort((a, b) => {
          if (a.stayDate !== b.stayDate) {
            return String(a.stayDate).localeCompare(String(b.stayDate));
          }
          if (a.clientLabel !== b.clientLabel) {
            return String(a.clientLabel).localeCompare(String(b.clientLabel), sortLocale);
          }
          return String(a.categoryName).localeCompare(String(b.categoryName), sortLocale);
        });

        const uniqueDates = [...new Set(merged.map((row) => row.stayDate))].filter(Boolean).sort();
        setSummaryAllocations(merged);
        setDates(uniqueDates);
        if (failed.length > 0) {
          setSummaryError(allocationCopy.alerts.partialSummary(failed.length, requests.length));
        }
      } catch (error) {
        if (requestId !== summaryFetchSequenceRef.current) return;
        setSummaryAllocations([]);
        setDates([]);
        setSummaryError(
          getLocalizedRequestError(error, allocationCopy.alerts.loadSummary, languageId)
        );
      } finally {
        if (!silent && requestId === summaryFetchSequenceRef.current) setSummaryLoading(false);
      }
    },
    [allocationCopy.alerts, allocationCopy.fallback.unknownClient, businessUnitId, clients, endDate, languageId, normalizeRow, sortLocale, startDate]
  );

  useEffect(() => {
    const syncSessionFromStorage = () => setCurrentUser(readStoredUser());
    window.addEventListener("palantir-session-updated", syncSessionFromStorage);
    window.addEventListener("storage", syncSessionFromStorage);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionFromStorage);
      window.removeEventListener("storage", syncSessionFromStorage);
    };
  }, []);

  useEffect(() => {
    void fetchRoomCategories();
  }, [fetchRoomCategories, crossTabRefreshToken]);

  useEffect(() => {
    void fetchClients();
  }, [fetchClients, crossTabRefreshToken]);

  useEffect(() => {
    if (!selectedClient || selectedClient === SUMMARY_CLIENT_VALUE) {
      void fetchSummaryAllocations();
      return;
    }
    void fetchAllocations();
  }, [fetchAllocations, fetchSummaryAllocations, selectedClient, crossTabRefreshToken]);

  useEffect(() => {
    setEditingCell(null);
    setEditingValue("");
    setSavingCellKey("");
    setOperationError("");
    setSummaryError("");
    setBulkModalOpen(false);
    setBulkError("");
  }, [selectedClient, startDate, endDate, businessUnitId]);

  useEffect(() => {
    if (!canEditAllocations && selectedClient !== SUMMARY_CLIENT_VALUE) {
      setSelectedClient(SUMMARY_CLIENT_VALUE);
    }
  }, [canEditAllocations, selectedClient]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const pushToast = useCallback((message) => {
    const text = String(message || "").trim();
    if (!text) return;

    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToastMessage(text);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage("");
      toastTimerRef.current = null;
    }, 2200);
  }, []);

  const selectedClientName = useMemo(() => {
    return clients.find((client) => client.value === selectedClient)?.label || "";
  }, [clients, selectedClient]);

  const isSummaryMode = !selectedClient || selectedClient === SUMMARY_CLIENT_VALUE;
  const managedClients = useMemo(
    () => clients.filter((client) => client.value !== SUMMARY_CLIENT_VALUE),
    [clients]
  );

  const categories = useMemo(() => {
    const grouped = new Map();

    allocations.forEach((row) => {
      const categoryId = String(row.roomCategoryId || "");
      if (!categoryId) return;

      if (!grouped.has(categoryId)) {
        grouped.set(categoryId, {
          categoryId: row.roomCategoryId,
          categoryName: row.categoryName || allocationCopy.fallback.category(categoryId),
          totalRooms: row.totalRooms,
          cellsByDate: {},
        });
      }

      const category = grouped.get(categoryId);
      category.totalRooms = Math.max(category.totalRooms || 0, row.totalRooms || 0);
      category.cellsByDate[row.stayDate] = row;
    });

    return Array.from(grouped.values()).sort((a, b) =>
      String(a.categoryName || "").localeCompare(String(b.categoryName || ""), sortLocale, {
        sensitivity: "base",
      })
    );
  }, [allocationCopy.fallback, allocations, sortLocale]);

  const summaryGroups = useMemo(() => {
    const grouped = new Map();

    summaryAllocations.forEach((row) => {
      const key = `${row.clientValue}::${row.roomCategoryId}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          clientValue: row.clientValue,
          clientLabel: row.clientLabel || allocationCopy.fallback.unknownClient,
          categoryId: row.roomCategoryId,
          categoryName: row.categoryName || allocationCopy.fallback.category(row.roomCategoryId),
          totalRooms: row.totalRooms,
          cellsByDate: {},
        });
      }

      const item = grouped.get(key);
      item.totalRooms = Math.max(item.totalRooms || 0, row.totalRooms || 0);
      item.cellsByDate[row.stayDate] = row;
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const clientCmp = String(a.clientLabel).localeCompare(String(b.clientLabel), sortLocale, {
        sensitivity: "base",
      });
      if (clientCmp !== 0) return clientCmp;
      return String(a.categoryName).localeCompare(String(b.categoryName), sortLocale, {
        sensitivity: "base",
      });
    });
  }, [allocationCopy.fallback, sortLocale, summaryAllocations]);

  const summaryAttentionRows = useMemo(() => {
    const severity = (cell) => {
      if (cell.isLimitReached || cell.isOverLimit) return 0;
      if (cell.isCategorySoldOut || cell.availableRooms == null) return 1;
      if (cell.isNearLimit) return 2;
      return 3;
    };

    return summaryAllocations
      .filter(
        (cell) =>
          cell.isRestrictedCap || cell.isLimitReached || cell.isNearLimit || cell.isCategorySoldOut || cell.availableRooms == null
      )
      .sort((a, b) => {
        if (a.stayDate !== b.stayDate) return String(a.stayDate).localeCompare(String(b.stayDate));
        const severityDiff = severity(a) - severity(b);
        if (severityDiff !== 0) return severityDiff;
        const clientDiff = String(a.clientLabel || "").localeCompare(
          String(b.clientLabel || ""),
          sortLocale,
          { sensitivity: "base" }
        );
        if (clientDiff !== 0) return clientDiff;
        return String(a.categoryName || "").localeCompare(
          String(b.categoryName || ""),
          sortLocale,
          { sensitivity: "base" }
        );
      });
  }, [sortLocale, summaryAllocations]);

  const globalStats = useMemo(() => {
    const visibleCells = [];

    categories.forEach((category) => {
      dates.forEach((date) => {
        const cell = category.cellsByDate[date];
        if (cell) visibleCells.push(cell);
      });
    });

    const totalSold = visibleCells.reduce((sum, cell) => sum + Math.max(0, cell.soldRooms), 0);
    const totalLimit = visibleCells.reduce((sum, cell) => sum + Math.max(0, cell.limitRooms), 0);
    const restrictedCells = visibleCells.filter((cell) => cell.isRestrictedCap).length;
    const stopSellCells = visibleCells.filter((cell) => cell.isLimitReached).length;
    const nearLimitCells = visibleCells.filter((cell) => cell.isNearLimit).length;
    const occupancyPct = totalLimit > 0 ? Math.round((totalSold / totalLimit) * 100) : 0;

    return {
      restrictedCells,
      stopSellCells,
      nearLimitCells,
      totalSold,
      totalLimit,
      occupancyPct,
    };
  }, [categories, dates]);

  const summaryStats = useMemo(() => {
    const cells = [];
    summaryGroups.forEach((group) => {
      dates.forEach((date) => {
        const cell = group.cellsByDate[date];
        if (cell) cells.push(cell);
      });
    });

    const clientsCount = new Set(summaryGroups.map((group) => group.clientValue)).size;
    const categoriesCount = new Set(summaryGroups.map((group) => group.categoryId)).size;
    const restrictedCaps = cells.filter(
      (cell) => Number(cell.limitRooms || 0) < Number(cell.totalRooms || 0)
    ).length;
    const autoStopRows = cells.filter((cell) => cell.isLimitReached).length;
    const nearLimitCells = cells.filter((cell) => cell.isNearLimit).length;

    return {
      clientsCount,
      categoriesCount,
      restrictedCaps,
      autoStopRows,
      nearLimitCells,
    };
  }, [dates, summaryGroups]);

  const loadedRangeLabel = useMemo(() => {
    if (dates.length > 0) return `${dates[0]} -> ${dates[dates.length - 1]}`;
    return `${startDate} -> ${endDate}`;
  }, [dates, endDate, startDate]);

  const selectedRangeDays = useMemo(
    () => enumerateIsoDates(startDate, endDate).length,
    [endDate, startDate]
  );

  const bulkRangeDays = useMemo(
    () => enumerateIsoDates(bulkFromDate, bulkToDate).length,
    [bulkFromDate, bulkToDate]
  );
  const bulkPreviewCells = bulkCategoryIds.length * bulkRangeDays;

  const subtitle = isSummaryMode
    ? summaryLoading
      ? allocationCopy.subtitles.summaryLoading
      : allocationCopy.subtitles.summaryLoaded(
          summaryStats.clientsCount,
          summaryStats.categoriesCount,
          loadedRangeLabel
        )
    : loading
    ? allocationCopy.subtitles.loading
    : selectedClientName
    ? allocationCopy.subtitles.loaded(selectedClientName, categories.length, loadedRangeLabel)
    : allocationCopy.subtitles.empty;

  const handleStartDateChange = useCallback((nextStartDate) => {
    const normalizedStart = String(nextStartDate || "").slice(0, 10) || todayIso();
    setStartDate(normalizedStart);
    setEndDate((currentEndDate) => {
      const normalizedCurrentEnd = String(currentEndDate || "").slice(0, 10);
      if (!normalizedCurrentEnd || normalizedCurrentEnd < normalizedStart) return normalizedStart;
      return normalizedCurrentEnd;
    });
  }, []);

  const handleEndDateChange = useCallback(
    (nextEndDate) => {
      const normalizedEnd = String(nextEndDate || "").slice(0, 10);
      if (!normalizedEnd) {
        setEndDate(startDate);
        return;
      }
      if (normalizedEnd < startDate) {
        setEndDate(startDate);
        return;
      }
      setEndDate(normalizedEnd);
    },
    [startDate]
  );

  const showOverview = useCallback(() => {
    setSelectedClient(SUMMARY_CLIENT_VALUE);
  }, []);

  const showManageLimits = useCallback(() => {
    if (!canEditAllocations) return;
    setSelectedClient((current) => {
      if (current && current !== SUMMARY_CLIENT_VALUE) return current;
      return managedClients[0]?.value || "";
    });
  }, [canEditAllocations, managedClients]);

  const applyRangePreset = useCallback(
    (days) => {
      setEndDate(addDaysIso(startDate, days - 1));
    },
    [startDate]
  );

  const resetRangeToToday = useCallback(() => {
    const nextStart = todayIso();
    const days = selectedRangeDays || HORIZON_DAYS;
    setStartDate(nextStart);
    setEndDate(addDaysIso(nextStart, days - 1));
  }, [selectedRangeDays]);

  const openBulkModal = useCallback(() => {
    if (!canEditAllocations || isSummaryMode || !selectedClient) return;
    const defaultFrom = dates[0] || startDate;
    const defaultTo = dates[dates.length - 1] || endDate || defaultFrom;
    const normalizedTo = defaultTo < defaultFrom ? defaultFrom : defaultTo;

    setBulkFromDate(defaultFrom);
    setBulkToDate(normalizedTo);
    setBulkRuleMode("limit");
    setBulkCapValue("");
    setBulkCategoryIds([]);
    setBulkError("");
    setBulkModalOpen(true);
  }, [canEditAllocations, dates, endDate, isSummaryMode, selectedClient, startDate]);

  const closeBulkModal = useCallback(() => {
    if (bulkApplying) return;
    setBulkModalOpen(false);
    setBulkError("");
  }, [bulkApplying]);

  useEffect(() => {
    if (!bulkModalOpen) return undefined;

    const dialog = bulkModalRef.current;
    const previousFocus = document.activeElement;
    const trigger = bulkTriggerRef.current;
    const focusableSelector =
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)';
    const firstFocusable = dialog?.querySelector(focusableSelector);
    (firstFocusable || dialog)?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (!bulkApplying) closeBulkModal();
        return;
      }

      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(focusableSelector));
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const restoreTarget = trigger || previousFocus;
      if (restoreTarget && typeof restoreTarget.focus === "function") restoreTarget.focus();
    };
  }, [bulkApplying, bulkModalOpen, closeBulkModal]);

  const applyBulkUpdate = useCallback(async () => {
    if (!canEditAllocations || bulkApplying || isSummaryMode || !selectedClient) return;

    const restoringFullInventory = bulkRuleMode === "full";
    const parsedCap = restoringFullInventory
      ? null
      : Number(String(bulkCapValue || "").trim());
    if (!restoringFullInventory && (!Number.isInteger(parsedCap) || parsedCap < 0)) {
      setBulkError(allocationCopy.alerts.bulkCapInteger);
      return;
    }

    const targetFrom = String(bulkFromDate || "").slice(0, 10);
    const targetTo = String(bulkToDate || "").slice(0, 10);
    if (!targetFrom || !targetTo || targetTo < targetFrom) {
      setBulkError(allocationCopy.alerts.validDateRange);
      return;
    }

    const selectedCategories = categories.filter((category) =>
      bulkCategoryIds.includes(String(category.categoryId))
    );
    if (selectedCategories.length === 0) {
      setBulkError(allocationCopy.alerts.selectCategory);
      return;
    }

    const invalidCategories = restoringFullInventory
      ? []
      : selectedCategories.filter(
          (category) => parsedCap > Number(category.totalRooms || 0)
        );
    if (invalidCategories.length > 0) {
      setBulkError(
        allocationCopy.alerts.capExceedsInventory(
          invalidCategories
          .slice(0, 3)
          .map((category) => category.categoryName)
          .join(", "),
          invalidCategories.length > 3
        )
      );
      return;
    }

    const targetDates = enumerateIsoDates(targetFrom, targetTo);
    if (targetDates.length === 0) {
      setBulkError(allocationCopy.alerts.noDatesToUpdate);
      return;
    }

    const payloads = [];
    selectedCategories.forEach((category) => {
      targetDates.forEach((date) => {
        payloads.push({
          business_unit_id: Number(businessUnitId),
          room_category_id: Number(category.categoryId),
          stay_date: date,
          client_id: toClientPayloadValue(selectedClient),
          max_rooms_allowed: restoringFullInventory
            ? Number(category.totalRooms || 0)
            : parsedCap,
        });
      });
    });

    setBulkApplying(true);
    setBulkError("");
    let successCount = 0;
    const failures = [];
    const queue = [...payloads];
    const workers = Math.min(6, queue.length);

    try {
      await Promise.all(
        Array.from({ length: workers }, async () => {
          while (queue.length > 0) {
            const nextPayload = queue.shift();
            if (!nextPayload) break;
            try {
              await axios.patch("/api/client-category-allocation", nextPayload);
              successCount += 1;
            } catch (error) {
              failures.push({
                payload: nextPayload,
                message: getLocalizedRequestError(
                  error,
                  allocationCopy.fallback.unknownError,
                  languageId
                ),
              });
            }
          }
        })
      );

      if (successCount > 0) {
        pushToast(allocationCopy.toasts.bulkApplied(successCount));
        await fetchAllocations({ silent: true });
      }

      if (failures.length > 0) {
        const sample = failures[0];
        setBulkError(
          allocationCopy.alerts.bulkFailed(
            failures.length,
            payloads.length,
            sample.payload.stay_date,
            sample.payload.room_category_id,
            sample.message
          )
        );
        return;
      }

      setBulkModalOpen(false);
    } finally {
      setBulkApplying(false);
    }
  }, [
    bulkCapValue,
    bulkCategoryIds,
    bulkFromDate,
    bulkRuleMode,
    bulkToDate,
    businessUnitId,
    bulkApplying,
    canEditAllocations,
    categories,
    allocationCopy.alerts,
    allocationCopy.fallback.unknownError,
    allocationCopy.toasts,
    fetchAllocations,
    isSummaryMode,
    languageId,
    pushToast,
    selectedClient,
  ]);

  const startEdit = (cell) => {
    if (!canEditAllocations || savingCellKey) return;
    setOperationError("");
    setEditingCell({
      roomCategoryId: cell.roomCategoryId,
      stayDate: cell.stayDate,
      totalRooms: Math.max(0, toInt(cell.totalRooms, 0)),
    });
    setEditingValue(String(cell.limitRooms));
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditingValue("");
  };

  const commitEdit = useCallback(async () => {
    if (!canEditAllocations || savingCellKey || isSummaryMode) return;
    if (!editingCell || !selectedClient) return;

    const trimmed = String(editingValue || "").trim();
    if (!trimmed.length) {
      setOperationError(allocationCopy.alerts.maxRoomsRequired);
      return;
    }

    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < 0) {
      setOperationError(allocationCopy.alerts.maxRoomsInteger);
      return;
    }

    const targetCell = allocations.find(
      (row) =>
        row.roomCategoryId === editingCell.roomCategoryId &&
        row.stayDate === editingCell.stayDate
    );
    if (!targetCell) {
      setOperationError(allocationCopy.alerts.validateCap);
      return;
    }

    const inventoryCap = Math.max(0, Number(targetCell.totalRooms || 0));
    if (parsed > inventoryCap) {
      setOperationError(allocationCopy.alerts.capCannotExceed(inventoryCap));
      return;
    }

    const nextLimit = parsed;

    const key = buildCellKey(editingCell.roomCategoryId, editingCell.stayDate);
    setSavingCellKey(key);
    setOperationError("");

    try {
      const response = await axios.patch("/api/client-category-allocation", {
        business_unit_id: Number(businessUnitId),
        room_category_id: Number(editingCell.roomCategoryId),
        stay_date: editingCell.stayDate,
        client_id: toClientPayloadValue(selectedClient),
        max_rooms_allowed: nextLimit,
      });

      setEditingCell(null);
      setEditingValue("");
      const serverMessage =
        languageId === "en"
          ? response?.data?.automation_message || response?.data?.message
          : "";
      pushToast(
        serverMessage || allocationCopy.toasts.capSaved
      );
      await fetchAllocations({ silent: true });
    } catch (error) {
      setOperationError(
        getLocalizedRequestError(error, allocationCopy.alerts.saveCap, languageId)
      );
    } finally {
      setSavingCellKey("");
    }
  }, [
    allocations,
    allocationCopy.alerts,
    allocationCopy.toasts.capSaved,
    businessUnitId,
    canEditAllocations,
    editingCell,
    editingValue,
    fetchAllocations,
    isSummaryMode,
    languageId,
    pushToast,
    savingCellKey,
    selectedClient,
  ]);

  const getStatusLabel = (cell) => {
    const status = getCellStatusKey(cell);
    if (status === "reached") return allocationCopy.table.statusReached;
    if (status === "soldOut") return allocationCopy.table.statusSoldOut;
    if (status === "unknown") return allocationCopy.table.statusUnknown;
    if (status === "near") return allocationCopy.table.statusNear;
    if (status === "active") return allocationCopy.table.statusActive;
    return allocationCopy.legend.openCapacity;
  };

  return (
    <div className={"cca-page" + (isLightTheme ? " cca-page--light" : "")}>
      {toastMessage ? (
        <div className="cca-toastOverlay" role="status" aria-live="polite">
          <div className="cca-toastCard">
            <FiCheck size={16} aria-hidden="true" />
            {toastMessage}
          </div>
        </div>
      ) : null}

      {bulkModalOpen ? (
        <div
          className="cca-modalOverlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeBulkModal();
          }}
        >
          <div
            ref={bulkModalRef}
            className="cca-modalCard"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cca-bulk-title"
            tabIndex={-1}
          >
            <div className="cca-modalHeader">
              <div>
                <div id="cca-bulk-title" className="cca-modalTitle">
                  {allocationCopy.modal.title}
                </div>
                <div className="cca-modalSubtitle">
                  {allocationCopy.modal.subtitle(
                    selectedClientName,
                    allocationCopy.fallback.clientLabel
                  )}
                </div>
              </div>
              <button
                type="button"
                className="cca-modalClose"
                onClick={closeBulkModal}
                disabled={bulkApplying}
                aria-label={allocationCopy.modal.close}
                title={allocationCopy.modal.close}
              >
                <FiX size={17} aria-hidden="true" />
              </button>
            </div>

            <div className="cca-modalBody">
              <fieldset disabled={bulkApplying}>
                <section className="cca-modalSection" aria-labelledby="cca-rule-step">
                  <div className="cca-modalSectionHead">
                    <div>
                      <div id="cca-rule-step" className="cca-modalStep">
                        {allocationCopy.modal.stepRule}
                      </div>
                      <div className="cca-modalStepHint">{allocationCopy.modal.ruleHint}</div>
                    </div>
                  </div>

                  <div
                    className="cca-ruleChoices"
                    role="radiogroup"
                    aria-label={allocationCopy.modal.stepRule}
                  >
                    <label
                      className={
                        "cca-ruleChoice" + (bulkRuleMode === "limit" ? " is-selected" : "")
                      }
                    >
                      <input
                        type="radio"
                        name="cca-bulk-rule"
                        value="limit"
                        checked={bulkRuleMode === "limit"}
                        onChange={() => setBulkRuleMode("limit")}
                      />
                      <span>
                        <strong>{allocationCopy.modal.limitRule}</strong>
                        <small>{allocationCopy.modal.limitRuleHint}</small>
                      </span>
                    </label>
                    <label
                      className={
                        "cca-ruleChoice" + (bulkRuleMode === "full" ? " is-selected" : "")
                      }
                    >
                      <input
                        type="radio"
                        name="cca-bulk-rule"
                        value="full"
                        checked={bulkRuleMode === "full"}
                        onChange={() => setBulkRuleMode("full")}
                      />
                      <span>
                        <strong>{allocationCopy.modal.fullInventoryRule}</strong>
                        <small>{allocationCopy.modal.fullInventoryRuleHint}</small>
                      </span>
                    </label>
                  </div>

                  <div
                    className={
                      "cca-modalGrid" + (bulkRuleMode === "full" ? " is-two" : "")
                    }
                  >
                    <label className="cca-field">
                      <span>{allocationCopy.labels.fromDate}</span>
                      <input
                        className="cca-input"
                        type="date"
                        value={bulkFromDate}
                        max={bulkToDate || undefined}
                        onChange={(event) => {
                          const nextFrom = String(event.target.value || "").slice(0, 10);
                          setBulkFromDate(nextFrom);
                          if (!bulkToDate || bulkToDate < nextFrom) setBulkToDate(nextFrom);
                        }}
                      />
                    </label>

                    <label className="cca-field">
                      <span>{allocationCopy.labels.toDate}</span>
                      <input
                        className="cca-input"
                        type="date"
                        value={bulkToDate}
                        min={bulkFromDate || undefined}
                        onChange={(event) => {
                          const nextTo = String(event.target.value || "").slice(0, 10);
                          if (!nextTo || (bulkFromDate && nextTo < bulkFromDate)) {
                            setBulkToDate(bulkFromDate);
                            return;
                          }
                          setBulkToDate(nextTo);
                        }}
                      />
                    </label>

                    {bulkRuleMode === "limit" ? (
                      <label className="cca-field">
                        <span>{allocationCopy.labels.capValue}</span>
                        <input
                          className="cca-input"
                          type="number"
                          min="0"
                          step="1"
                          value={bulkCapValue}
                          onChange={(event) => setBulkCapValue(event.target.value)}
                          placeholder={allocationCopy.placeholders.capExample}
                        />
                      </label>
                    ) : null}
                  </div>

                  {bulkRuleMode === "limit" && String(bulkCapValue).trim() === "0" ? (
                    <div className="cca-alert" role="alert">
                      <FiAlertCircle size={15} aria-hidden="true" />
                      <span>{allocationCopy.modal.zeroWarning}</span>
                    </div>
                  ) : null}
                </section>

                <section className="cca-modalSection" aria-labelledby="cca-categories-step">
                  <div className="cca-modalSectionHead">
                    <div>
                      <div id="cca-categories-step" className="cca-modalStep">
                        {allocationCopy.modal.stepCategories}
                      </div>
                      <div className="cca-modalStepHint">{allocationCopy.modal.categoryHint}</div>
                    </div>
                    <span className="cca-bulkMeta">
                      {allocationCopy.modal.categoriesSelected(
                        bulkCategoryIds.length,
                        categories.length
                      )}
                    </span>
                  </div>

                  <div className="cca-bulkBar">
                    <button
                      type="button"
                      className="cca-bulkBtn"
                      onClick={() =>
                        setBulkCategoryIds(
                          categories.map((category) => String(category.categoryId))
                        )
                      }
                    >
                      {allocationCopy.buttons.selectAll}
                    </button>
                    <button
                      type="button"
                      className="cca-bulkBtn"
                      onClick={() => setBulkCategoryIds([])}
                    >
                      {allocationCopy.buttons.clear}
                    </button>
                  </div>

                  <div className="cca-bulkCategories">
                    {categories.map((category) => {
                      const id = String(category.categoryId);
                      const checked = bulkCategoryIds.includes(id);
                      return (
                        <label
                          key={"bulk-category-" + id}
                          className={"cca-bulkCategory" + (checked ? " is-selected" : "")}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setBulkCategoryIds((current) =>
                                current.includes(id)
                                  ? current.filter((value) => value !== id)
                                  : [...current, id]
                              );
                            }}
                          />
                          <span className="cca-bulkCategoryName">
                            {category.categoryName}
                          </span>
                          <span className="cca-bulkCategoryMeta">
                            {allocationCopy.modal.categoryRooms(category.totalRooms)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>
              </fieldset>

              <div className="cca-bulkPreview">
                <FiInfo size={15} aria-hidden="true" />
                <span>
                  {allocationCopy.modal.preview(
                    bulkCategoryIds.length,
                    bulkRangeDays,
                    bulkPreviewCells
                  )}
                </span>
              </div>

              {bulkError ? (
                <div className="cca-alert" role="alert">
                  <FiAlertCircle size={15} aria-hidden="true" />
                  <span>{bulkError}</span>
                </div>
              ) : null}
            </div>

            <div className="cca-modalFooter">
              <button
                type="button"
                className="cca-modalAction"
                onClick={closeBulkModal}
                disabled={bulkApplying}
              >
                {allocationCopy.buttons.cancel}
              </button>
              <button
                type="button"
                className="cca-modalAction is-primary"
                onClick={() => void applyBulkUpdate()}
                disabled={
                  bulkApplying ||
                  bulkPreviewCells === 0 ||
                  (bulkRuleMode === "limit" && String(bulkCapValue).trim().length === 0)
                }
              >
                {bulkApplying
                  ? allocationCopy.buttons.applying
                  : allocationCopy.buttons.applyBulkUpdate}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="cca-card">
        <header className="cca-header">
          <div className="cca-headingBlock">
            <h2 className="cca-title">{allocationCopy.title}</h2>
            <div className="cca-subtitle">{allocationCopy.purpose}</div>
            <div className="cca-scopeMeta">{subtitle}</div>
          </div>

          <div
            className="cca-viewSwitch"
            role="group"
            aria-label={allocationCopy.view.label}
          >
            <button
              type="button"
              className={"cca-viewButton" + (isSummaryMode ? " is-active" : "")}
              aria-pressed={isSummaryMode}
              onClick={showOverview}
            >
              <FiBarChart2 size={15} aria-hidden="true" />
              {allocationCopy.view.overview}
            </button>
            <button
              type="button"
              className={"cca-viewButton" + (!isSummaryMode ? " is-active" : "")}
              aria-pressed={!isSummaryMode}
              disabled={!canEditAllocations || managedClients.length === 0}
              onClick={showManageLimits}
              title={!canEditAllocations ? allocationCopy.view.readOnly : undefined}
            >
              <FiEdit3 size={15} aria-hidden="true" />
              {allocationCopy.view.manage}
            </button>
          </div>
        </header>

        <section className="cca-controls" aria-label={allocationCopy.controls.dateRange}>
          <div className="cca-controlGroup">
            <div className="cca-controlGroupTitle">
              <FiCalendar size={14} aria-hidden="true" />
              {allocationCopy.controls.dateRange}
            </div>
            <div className="cca-dateControlRow">
              <div className="cca-dateFields">
                <label className="cca-field">
                  <span>{allocationCopy.labels.fromDate}</span>
                  <input
                    className="cca-input"
                    type="date"
                    value={startDate}
                    max={endDate}
                    onChange={(event) => handleStartDateChange(event.target.value)}
                  />
                </label>
                <label className="cca-field">
                  <span>{allocationCopy.labels.toDate}</span>
                  <input
                    className="cca-input"
                    type="date"
                    value={endDate}
                    min={startDate}
                    onChange={(event) => handleEndDateChange(event.target.value)}
                  />
                </label>
              </div>
              <div
                className="cca-rangePresets"
                role="group"
                aria-label={allocationCopy.controls.quickRange}
              >
                <button
                  type="button"
                  className="cca-rangeButton"
                  onClick={resetRangeToToday}
                >
                  {allocationCopy.buttons.today}
                </button>
                {[7, 14, 30].map((days) => (
                  <button
                    key={"range-" + days}
                    type="button"
                    className={
                      "cca-rangeButton" + (selectedRangeDays === days ? " is-active" : "")
                    }
                    aria-pressed={selectedRangeDays === days}
                    onClick={() => applyRangePreset(days)}
                    title={allocationCopy.rangePreset(days)}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="cca-controlGroup">
            <div className="cca-controlGroupTitle">
              {isSummaryMode ? allocationCopy.view.overview : allocationCopy.labels.client}
            </div>
            {isSummaryMode ? (
              <div className="cca-controlGroupHint">
                {canEditAllocations
                  ? allocationCopy.view.overviewHint
                  : allocationCopy.view.readOnly}
              </div>
            ) : (
              <label className="cca-field cca-field--client">
                <span className="cca-srOnly">{allocationCopy.labels.client}</span>
                <select
                  className="cca-select"
                  value={selectedClient}
                  onChange={(event) => setSelectedClient(event.target.value)}
                >
                  {managedClients.length === 0 ? (
                    <option value="">{allocationCopy.placeholders.noClientsAvailable}</option>
                  ) : null}
                  {managedClients.map((client) => (
                    <option key={client.value} value={client.value}>
                      {client.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="cca-controlActions">
            <button
              type="button"
              className="cca-btn"
              disabled={isSummaryMode ? summaryLoading : loading || !selectedClient}
              onClick={() => {
                if (isSummaryMode) {
                  void fetchSummaryAllocations();
                  return;
                }
                void fetchAllocations();
              }}
            >
              <FiRefreshCw size={14} aria-hidden="true" />
              {allocationCopy.buttons.refresh}
            </button>

            {!isSummaryMode ? (
              <button
                ref={bulkTriggerRef}
                type="button"
                className="cca-btn is-primary"
                disabled={
                  !canEditAllocations || loading || !selectedClient || categories.length === 0
                }
                onClick={openBulkModal}
              >
                <FiSliders size={14} aria-hidden="true" />
                {allocationCopy.buttons.bulkUpdate}
              </button>
            ) : null}
          </div>
        </section>

        <section className="cca-stats" aria-label={allocationCopy.title}>
          <div className="cca-stat">
            <div className="cca-statLabel">
              {isSummaryMode
                ? allocationCopy.stats.summaryClients
                : allocationCopy.stats.detailRestricted}
            </div>
            <div className="cca-statValue">
              {isSummaryMode ? summaryStats.clientsCount : globalStats.restrictedCells}
            </div>
          </div>
          <div
            className={
              "cca-stat" +
              ((isSummaryMode ? summaryStats.restrictedCaps : globalStats.stopSellCells) > 0
                ? isSummaryMode
                  ? " is-attention"
                  : " is-danger"
                : "")
            }
          >
            <div className="cca-statLabel">
              {isSummaryMode
                ? allocationCopy.stats.summaryRestricted
                : allocationCopy.stats.detailStopSell}
            </div>
            <div className="cca-statValue">
              {isSummaryMode ? summaryStats.restrictedCaps : globalStats.stopSellCells}
            </div>
          </div>
          <div
            className={
              "cca-stat" +
              ((isSummaryMode ? summaryStats.autoStopRows : globalStats.nearLimitCells) > 0
                ? isSummaryMode
                  ? " is-danger"
                  : " is-attention"
                : "")
            }
          >
            <div className="cca-statLabel">
              {isSummaryMode
                ? allocationCopy.stats.summaryAutoStop
                : allocationCopy.stats.detailNear}
            </div>
            <div className="cca-statValue">
              {isSummaryMode ? summaryStats.autoStopRows : globalStats.nearLimitCells}
            </div>
          </div>
          <div
            className={
              "cca-stat" +
              (isSummaryMode && summaryStats.nearLimitCells > 0 ? " is-attention" : "")
            }
          >
            <div className="cca-statLabel">
              {isSummaryMode
                ? allocationCopy.stats.summaryNear
                : allocationCopy.stats.detailSoldVsCap}
            </div>
            <div className="cca-statValue">
              {isSummaryMode
                ? summaryStats.nearLimitCells
                : allocationCopy.stats.detailSoldVsCapValue(
                    globalStats.totalSold,
                    globalStats.totalLimit,
                    globalStats.occupancyPct
                  )}
            </div>
          </div>
        </section>

        <section className="cca-contextBar">
          <div className="cca-guide">
            <span className="cca-guideIcon" aria-hidden="true">
              <FiInfo size={15} />
            </span>
            <div>
              <div className="cca-guideTitle">
                {!canEditAllocations
                  ? allocationCopy.guide.readOnlyTitle
                  : isSummaryMode
                  ? allocationCopy.guide.overviewTitle
                  : allocationCopy.guide.detailTitle}
              </div>
              <div className="cca-guideText">
                {!canEditAllocations
                  ? allocationCopy.view.readOnly
                  : isSummaryMode
                  ? allocationCopy.guide.overviewText
                  : allocationCopy.guide.detailText}
              </div>
            </div>
          </div>

          <div className="cca-legend" aria-label={allocationCopy.title}>
            <span className="cca-legendItem">
              <span className="cca-swatch is-safe" aria-hidden="true" />
              {allocationCopy.legend.openCapacity}
            </span>
            <span className="cca-legendItem">
              <span className="cca-swatch is-restricted" aria-hidden="true" />
              {allocationCopy.legend.baselineHighlighted}
            </span>
            <span className="cca-legendItem">
              <span className="cca-swatch is-near" aria-hidden="true" />
              {allocationCopy.legend.nearLimit}
            </span>
            <span className="cca-legendItem">
              <span className="cca-swatch is-stop" aria-hidden="true" />
              {allocationCopy.legend.stopSellTriggered}
            </span>
          </div>
        </section>

        {operationError ? (
          <div className="cca-alert" role="alert">
            <FiAlertCircle size={15} aria-hidden="true" />
            <span>{operationError}</span>
          </div>
        ) : null}
        {loadError ? (
          <div className="cca-alert" role="alert">
            <FiAlertCircle size={15} aria-hidden="true" />
            <span>{loadError}</span>
          </div>
        ) : null}
        {clientsError ? (
          <div className="cca-alert" role="alert">
            <FiAlertCircle size={15} aria-hidden="true" />
            <span>{clientsError}</span>
          </div>
        ) : null}
        {categoriesError ? (
          <div className="cca-alert" role="alert">
            <FiAlertCircle size={15} aria-hidden="true" />
            <span>{categoriesError}</span>
          </div>
        ) : null}
        {summaryError && isSummaryMode ? (
          <div className="cca-alert" role="alert">
            <FiAlertCircle size={15} aria-hidden="true" />
            <span>{summaryError}</span>
          </div>
        ) : null}

        <div className="cca-tableWrap">
          {isSummaryMode ? (
            summaryLoading ? (
              <div className="cca-state" role="status" aria-live="polite">
                <div>
                  <div className="cca-spinner" aria-hidden="true" />
                  <div className="cca-stateTitle">
                    {allocationCopy.states.summaryLoadingTitle}
                  </div>
                  <div className="cca-stateMeta">
                    {allocationCopy.states.summaryLoadingMeta}
                  </div>
                </div>
              </div>
            ) : summaryGroups.length === 0 || dates.length === 0 ? (
              <div className="cca-state">
                <div>
                  <div className="cca-stateIcon" aria-hidden="true">
                    <FiBarChart2 size={20} />
                  </div>
                  <div className="cca-stateTitle">
                    {allocationCopy.states.summaryEmptyTitle}
                  </div>
                  <div className="cca-stateMeta">{allocationCopy.states.summaryEmptyMeta}</div>
                </div>
              </div>
            ) : summaryAttentionRows.length === 0 ? (
              <div className="cca-state">
                <div>
                  <div className="cca-stateIcon" aria-hidden="true">
                    <FiCheck size={20} />
                  </div>
                  <div className="cca-stateTitle">
                    {allocationCopy.states.attentionEmptyTitle}
                  </div>
                  <div className="cca-stateMeta">
                    {allocationCopy.states.attentionEmptyMeta}
                  </div>
                </div>
              </div>
            ) : (
              <table className="cca-attentionTable">
                <caption className="cca-srOnly">{allocationCopy.guide.overviewTitle}</caption>
                <thead>
                  <tr>
                    <th scope="col">{allocationCopy.table.client}</th>
                    <th scope="col">{allocationCopy.table.roomCategory}</th>
                    <th scope="col">{allocationCopy.table.overviewDate}</th>
                    <th scope="col">{allocationCopy.table.sold}</th>
                    <th scope="col">{allocationCopy.table.limit}</th>
                    <th scope="col">{allocationCopy.table.hotelAvailability}</th>
                    <th scope="col">{allocationCopy.table.sellable}</th>
                    <th scope="col">{allocationCopy.table.status}</th>
                    <th scope="col">{allocationCopy.table.action}</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryAttentionRows.map((cell) => {
                    const statusKey = getCellStatusKey(cell);
                    return (
                      <tr
                        key={
                          String(cell.clientValue) +
                          "-" +
                          String(cell.roomCategoryId) +
                          "-" +
                          cell.stayDate
                        }
                      >
                        <td>
                          <strong>{cell.clientLabel}</strong>
                        </td>
                        <td>{cell.categoryName}</td>
                        <td className="cca-numberCell">
                          {toDayHeader(cell.stayDate, calendarLocale)}
                        </td>
                        <td className="cca-numberCell">{cell.soldRooms}</td>
                        <td className="cca-numberCell">{cell.limitRooms}</td>
                        <td className="cca-numberCell">{cell.availableRooms ?? "—"}</td>
                        <td className="cca-numberCell">{cell.effectiveSellable ?? "—"}</td>
                        <td>
                          <span className={"cca-statusBadge cca-statusBadge--" + statusKey}>
                            {getStatusLabel(cell)}
                          </span>
                        </td>
                        <td>
                          {canEditAllocations ? (
                            <button
                              type="button"
                              className="cca-tableAction"
                              onClick={() => setSelectedClient(cell.clientValue)}
                            >
                              {allocationCopy.table.manage}
                            </button>
                          ) : (
                            <span aria-label={allocationCopy.view.readOnly}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          ) : loading ? (
            <div className="cca-state" role="status" aria-live="polite">
              <div>
                <div className="cca-spinner" aria-hidden="true" />
                <div className="cca-stateTitle">{allocationCopy.states.loadingTitle}</div>
                <div className="cca-stateMeta">{allocationCopy.states.loadingMeta}</div>
              </div>
            </div>
          ) : !selectedClient ? (
            <div className="cca-state">
              <div>
                <div className="cca-stateIcon" aria-hidden="true">
                  <FiEdit3 size={20} />
                </div>
                <div className="cca-stateTitle">
                  {allocationCopy.states.selectClientTitle}
                </div>
                <div className="cca-stateMeta">{allocationCopy.states.selectClientMeta}</div>
              </div>
            </div>
          ) : categories.length === 0 || dates.length === 0 ? (
            <div className="cca-state">
              <div>
                <div className="cca-stateIcon" aria-hidden="true">
                  <FiInfo size={20} />
                </div>
                <div className="cca-stateTitle">{allocationCopy.states.emptyTitle}</div>
                <div className="cca-stateMeta">{allocationCopy.states.emptyMeta}</div>
              </div>
            </div>
          ) : (
            <table className="cca-table">
              <caption className="cca-srOnly">{allocationCopy.guide.detailTitle}</caption>
              <colgroup>
                <col className="cca-width1" />
                {dates.map((date) => (
                  <col key={"col-" + date} className="cca-widthDay" />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className="cca-sticky1" scope="col">
                    <div className="cca-thMain">{allocationCopy.table.roomCategory}</div>
                    <div className="cca-thSub">
                      {allocationCopy.table.inventoryBaseline}
                    </div>
                  </th>
                  {dates.map((date) => (
                    <th key={"header-" + date} scope="col">
                      <div className="cca-thMain">
                        {toDayHeader(date, calendarLocale)}
                      </div>
                      <div className="cca-thSub">{date}</div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {categories.map((category, index) => (
                  <tr
                    key={String(category.categoryId)}
                    className={index > 0 ? "cca-rowSeparator" : ""}
                  >
                    <th className="cca-sticky1 cca-categoryCell" scope="row">
                      <div className="cca-categoryName">{category.categoryName}</div>
                      <div className="cca-categoryMeta">
                        {allocationCopy.table.inventory(category.totalRooms)}
                      </div>
                    </th>

                    {dates.map((date) => {
                      const cell = category.cellsByDate[date];
                      if (!cell) {
                        return (
                          <td
                            key={"empty-" + category.categoryId + "-" + date}
                            className="cca-dayCell cca-dayCell--empty"
                            aria-label={allocationCopy.table.noData}
                          >
                            <span aria-hidden="true">—</span>
                          </td>
                        );
                      }

                      const cellKey = buildCellKey(cell.roomCategoryId, cell.stayDate);
                      const isEditing =
                        editingCell?.roomCategoryId === cell.roomCategoryId &&
                        editingCell?.stayDate === cell.stayDate;
                      const isSaving = savingCellKey === cellKey;
                      const toneClass = getCellToneClass(cell);
                      const utilizationPct = Math.round(cell.utilizationRatio * 100);
                      const cellTitle = [
                        category.categoryName + " - " + date,
                        allocationCopy.table.soldRooms(cell.soldRooms),
                        allocationCopy.table.maxAllowed(cell.limitRooms),
                        allocationCopy.table.categoryAvailability(cell.availableRooms ?? "—"),
                        allocationCopy.table.sellableNow(cell.effectiveSellable ?? "—"),
                        getStatusLabel(cell),
                      ].join("\n");

                      return (
                        <td
                          key={String(category.categoryId) + "-" + date}
                          className={
                            "cca-dayCell " +
                            toneClass +
                            (cell.isRestrictedCap ? " is-restricted" : "") +
                            (isEditing ? " is-editing" : "")
                          }
                          title={cellTitle}
                        >
                          <div className="cca-cellBox">
                            {isSaving ? (
                              <span className="cca-savingDot" aria-hidden="true" />
                            ) : null}

                            {isEditing ? (
                              <div className="cca-editor">
                                <input
                                  className="cca-editorInput"
                                  type="number"
                                  min="0"
                                  max={Math.max(0, Number(cell.totalRooms || 0))}
                                  step="1"
                                  value={editingValue}
                                  onChange={(event) => setEditingValue(event.target.value)}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                      event.preventDefault();
                                      void commitEdit();
                                    }
                                    if (event.key === "Escape") {
                                      event.preventDefault();
                                      cancelEdit();
                                    }
                                  }}
                                  aria-label={allocationCopy.labels.capValue}
                                  disabled={Boolean(savingCellKey)}
                                  autoFocus
                                />
                                <div className="cca-editorHint">
                                  {allocationCopy.table.maxCapForCategory(
                                    Math.max(0, Number(cell.totalRooms || 0))
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="cca-editorReset"
                                  onClick={() => setEditingValue(String(cell.totalRooms))}
                                  disabled={Boolean(savingCellKey)}
                                >
                                  {allocationCopy.buttons.fullInventory}
                                </button>
                                {String(editingValue).trim() === "0" ? (
                                  <div className="cca-editorWarning">
                                    {allocationCopy.table.zeroLimitWarning}
                                  </div>
                                ) : null}
                                <div className="cca-editorActions">
                                  <button
                                    type="button"
                                    className="cca-editorBtn is-save"
                                    onClick={() => void commitEdit()}
                                    disabled={Boolean(savingCellKey)}
                                  >
                                    {allocationCopy.buttons.save}
                                  </button>
                                  <button
                                    type="button"
                                    className="cca-editorBtn"
                                    onClick={cancelEdit}
                                    disabled={Boolean(savingCellKey)}
                                  >
                                    {allocationCopy.buttons.cancel}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="cca-cellMetric">
                                  <span>{cell.soldRooms}</span>
                                  <span className="cca-cellMetricSeparator">/</span>
                                  <span>{cell.limitRooms}</span>
                                </div>
                                <div className="cca-cellMetricLabel">
                                  {cell.isRestrictedCap
                                    ? allocationCopy.table.soldLimitLabel
                                    : allocationCopy.table.soldInventoryLabel}
                                </div>
                                <div className="cca-progress" aria-hidden="true">
                                  <span
                                    className="cca-progressFill"
                                    style={{
                                      width:
                                        Math.min(100, Math.max(0, utilizationPct)) + "%",
                                    }}
                                  />
                                </div>
                                <button
                                  type="button"
                                  className="cca-limitBtn"
                                  disabled={!canEditAllocations || Boolean(savingCellKey)}
                                  onClick={() => startEdit(cell)}
                                  aria-label={
                                    allocationCopy.table.editLimit +
                                    ": " +
                                    category.categoryName +
                                    ", " +
                                    date
                                  }
                                >
                                  {cell.isRestrictedCap
                                    ? allocationCopy.table.capButton(cell.limitRooms)
                                    : allocationCopy.table.noCap}
                                </button>
                                <div className="cca-dayMeta">
                                  {cell.effectiveSellable == null
                                    ? allocationCopy.table.statusUnknown
                                    : allocationCopy.table.sellableNow(cell.effectiveSellable)}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientAllocations;
